import { ref } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { useQueryList } from '@/composables/common/useQueryList';

/**
 * useZonasPegatinas — listado paginado y buscable de las zonas de una sesión
 * para descargar/imprimir pegatinas.
 *
 * Endpoint: `back.event.sesion.regiones` →
 *   GET /eventos/{evento_id}/sesiones/{sesion_id}/zonas-evento
 * Espera `{ filters: { search, page, per_page }, evento_id, sesion_id, teatro_id }`
 * y responde con el shape Laravel (data + meta con current_page, last_page, total…).
 *
 * La paginación y el debounce los aporta el composable genérico `useQueryList`.
 * Aquí solo se traduce el contrato del endpoint y se expone el estado que la
 * vista necesita.
 *
 * @param {Object} options
 * @param {number|import('vue').Ref<number>} options.eventoId Id del evento.
 * @param {number|import('vue').Ref<number>} options.sesionId Id de la sesión.
 * @param {Function} [options.fetcher] Inyectable para tests. Por defecto
 *   despacha la acción Vuex real. Debe resolver `{ data: { data, meta } }`.
 * @returns {{
 *   zonas: import('vue').Ref<{data:Array, meta:Object}>,
 *   loading: import('vue').Ref<boolean>,
 *   error: import('vue').Ref<string|null>,
 *   filters: import('vue').Ref<Object>,
 *   meta: import('vue').ComputedRef<Object>,
 *   columns: Array,
 *   getZonas: Function,
 *   imprimirUrl: Function
 * }}
 */
export function useZonasPegatinas({ eventoId, sesionId, fetcher } = {}) {
    const error = ref(null);

    /**
     * Normaliza un valor o ref a su valor plano.
     */
    const unwrap = value =>
        value && typeof value === 'object' && 'value' in value ? value.value : value;

    /**
     * Petición por defecto: despacha la acción Vuex del endpoint de zonas.
     * El backend responde `{ data: { data: [...], meta: {...} } }`.
     */
    async function dispatchZonas({ filters }) {
        const params = {
            filters: {
                search: filters.search,
                page: filters.page,
                per_page: filters.per_page,
            },
            evento_id: unwrap(eventoId),
            sesion_id: unwrap(sesionId),
            teatro_id: store.getters.getTeatro?.id,
        };

        const res = await store.dispatch(
            config.getVuexAlias('back.event.sesion.regiones'),
            params
        );

        return res.data || { data: [], meta: {} };
    }

    const doFetch = fetcher || dispatchZonas;

    /**
     * Fetcher que consume `useQueryList`. Publica el mensaje de error del
     * endpoint (que `useQueryList` silencia) y degrada a lista vacía para que
     * el listado no rompa.
     */
    async function zonalFetcher(params) {
        try {
            const result = await doFetch(params);
            error.value = null;
            return result;
        } catch (e) {
            error.value = e?.response?.data?.message || 'Error al cargar las zonas';
            return { data: [], meta: {} };
        }
    }

    // urlSync por defecto (true): useQueryList registra el watcher que refetchea
    // al cambiar search/page/per_page. La sincronización con la URL es inocua
    // para esta ruta (los ids viajan por params, no por query).
    const list = useQueryList({
        fetcher: zonalFetcher,
        listaIdSource: 'none',
    });

    const columns = [
        { key: 'id', label: 'ID', field: 'id', class: 'tw:w-16 tw:sm:w-32' },
        { key: 'name', label: 'Nombre', field: 'name', class: 'tw:w-72 tw:sm:w-auto' },
        { key: 'opciones', label: 'Opciones', centered: true, class: 'tw:w-36 tw:sm:w-64' },
    ];

    /**
     * URL de descarga/impresión de pegatinas de una zona.
     */
    function imprimirUrl(item) {
        return config.API_BASE + (item?.url_imprimir || '');
    }

    return {
        zonas: list.data,
        loading: list.loading,
        error,
        filters: list.filters,
        meta: list.meta,
        columns,
        getZonas: list.fetch,
        imprimirUrl,
    };
}
